import curl2Json, { type ResultJSON } from '@bany/curl-to-json'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@neo/primitives/dialog'
import type React from 'react'
import { useState } from 'react'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { defaultNodeData } from '../../config/nodes.config'
import { FlowNodeTypes, type HttpRequestNodeData } from '../../types'
import { preprocessCurlCommand, processBodyData } from '../../utils/http-request-utils'

interface CurlImportDialogProps {
  form: GenericFormType<HttpRequestNodeData['config']>
}

export const CurlImportDialog: React.FC<CurlImportDialogProps> = ({ form }) => {
  const [curlCommand, setCurlCommand] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState('')

  const handleImport = async () => {
    try {
      setError('')

      let parsedData: ResultJSON
      try {
        // Preprocess the curl command to handle Postman format
        const processedCommand = preprocessCurlCommand(curlCommand)
        parsedData = curl2Json(processedCommand)
      } catch (parseError) {
        throw new Error(
          `Failed to parse curl command: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`,
        )
      }

      // Get default values from nodes.config.tsx
      const defaultHttpRequest = (
        defaultNodeData[FlowNodeTypes.http_request] as HttpRequestNodeData
      ).config.http_request

      // Batching all changes into a single update prevents intermediate null/undefined states that cause Object.values errors

      const currentValues = { ...form.state.values }

      if (!currentValues.http_request || typeof currentValues.http_request !== 'object') {
        currentValues.http_request = { ...defaultHttpRequest }
      }

      const resetHttpRequest = {
        ...defaultHttpRequest,
        name: currentValues.http_request.name || defaultHttpRequest.name,
      }

      const finalHttpRequest = { ...resetHttpRequest }

      // Prefill URL and method
      if (parsedData.url) {
        finalHttpRequest.url = parsedData.url
      }

      // Determine method - if data is present but method is GET, it should be POST
      const method = parsedData.method?.toLowerCase() as 'get' | 'post' | 'put' | 'delete' | 'patch'
      if (method) {
        finalHttpRequest.method = method
      }

      // Handle authentication from parsed data
      let authFound = false

      // Check for both Authorization (capital A) and authorization (lowercase a)
      const authHeader = parsedData.header?.Authorization || parsedData.header?.authorization

      if (authHeader) {
        if (authHeader.startsWith('Basic ')) {
          // Decode base64 to get username:password
          const decoded = atob(authHeader.split(' ')[1])
          const [username, password] = decoded.split(':')

          finalHttpRequest.auth_type = 'basic_auth'
          finalHttpRequest.auth_params = {
            ...finalHttpRequest.auth_params,
            username,
            password,
          }
          authFound = true
        } else if (authHeader.startsWith('Bearer ') || authHeader.startsWith('Token ')) {
          finalHttpRequest.auth_type = 'bearer_token'
          finalHttpRequest.auth_params = {
            ...finalHttpRequest.auth_params,
            token: authHeader.split(' ')[1],
          }
          authFound = true
        }
      }

      // Custom handling for -u flag (basic auth) since @bany/curl-to-json doesn't parse it
      // Handle both quoted and unquoted formats: -u username:password or -u "username:password"
      const basicAuthMatch = curlCommand.match(/-u\s+(['"`]?)([^'"`\s]+):([^'"`\s]+)\1/)
      if (basicAuthMatch) {
        const [, , username, password] = basicAuthMatch

        finalHttpRequest.auth_type = 'basic_auth'
        finalHttpRequest.auth_params = {
          ...finalHttpRequest.auth_params,
          username,
          password,
        }
        authFound = true
      }

      // If no authentication is found, set to no auth
      if (!authFound) {
        finalHttpRequest.auth_type = 'no_auth'
      }

      // Prefill headers - ensure we always set a valid JSON string
      if (parsedData.header && Object.keys(parsedData.header).length > 0) {
        finalHttpRequest.headers = JSON.stringify(parsedData.header, null, 2)
      } else {
        // Ensure headers is always a valid JSON string, never null/undefined
        finalHttpRequest.headers = '{}'
      }

      // Prefill params - ensure we always set a valid JSON string
      if (parsedData.params && Object.keys(parsedData.params).length > 0) {
        finalHttpRequest.params = JSON.stringify(parsedData.params, null, 2)
      } else {
        // Ensure params is always a valid JSON string, never null/undefined
        finalHttpRequest.params = '{}'
      }

      // Prefill body data
      if (parsedData.data != null) {
        const { bodyType, bodyData } = processBodyData(parsedData.data)

        finalHttpRequest.body_type = bodyType as 'none' | 'form_data' | 'json' | 'form_urlencoded'
        finalHttpRequest.body = JSON.stringify({ [bodyType]: bodyData }, null, 2)
      }

      // Single atomic update - prevents any intermediate null states
      form.setFieldValue('http_request', finalHttpRequest)

      setIsOpen(false)
      setCurlCommand('')
    } catch (_err) {
      setError('Invalid curl command. Please check the format and try again.')
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="w-fit self-end hover:bg-transparent text-secondary pr-0"
        >
          Import cURL
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[444px] max-h-[600px] flex flex-col overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import cURL command</DialogTitle>
        </DialogHeader>
        <CodeEditor
          language="python"
          value={curlCommand}
          onChange={(value) => setCurlCommand(value)}
          className="border border-st2 rounded-md flex-1 overflow-auto min-h-[180px]"
          data-testid="code-editor-curl"
        />

        {error && <div className="text-s-400 text-status-danger">{error}</div>}
        <DialogFooter className="sm:justify-between sticky bottom-0 bg-background">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!curlCommand.trim()}>
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
